import { useRouter } from 'next/navigation';
import React, { FC } from 'react';
import styled from 'styled-components';

import { WithLegacy } from '~/_legacy/components/WithLegacy';
import { ContactButton } from '~/_legacy/components/atoms/button/contactButton';
import { StartDiagnosisButton } from '~/_legacy/components/atoms/button/startDiagnosisButton';
import { DiagnosisHeader } from '~/_legacy/components/organisms/diagnosis/diagnosisHeader';
import { Footer } from '~/_legacy/components/organisms/footer';
import { Header } from '~/_legacy/components/organisms/header/Header';

import { OnlyWeb } from '~/components/_for-next/OnlyWeb';
import { WithNextSeo } from '~/components/_for-next/WithNextSeo';
import { media } from '~/utils';

export default function Page() {
  return (
    <WithNextSeo title='社会課題診断'>
      <OnlyWeb>
        <WithLegacy>
          <AboutDiagnosis />
        </WithLegacy>
      </OnlyWeb>
    </WithNextSeo>
  );
}

const AboutDiagnosis: FC = () => {
  const router = useRouter();

  const contents: {
    subTitle: string;
    title: string;
    image: string;
    text: string;
  }[] = [
    {
      subTitle: 'うちの会社に何ができる？',
      title: '事業と社会課題とのマッチング',
      image: '/images/diagnosis/main1.png',
      text: `企業がより効果的に社会貢献を行っていくには、社会的活動の内容が事業の本質と合致していることが必要です。

この無料診断ツールでは、事業内容と活動地域から、御社が取り組むのに最もふさわしい社会課題やSDGsゴールを診断いたします。`,
    },
    {
      subTitle: 'どうやって診断しているの？',
      title: '豊富な経験とデータ + 御社の関心',
      image: '/images/diagnosis/main2.png',
      text: `事業内容および活動地域のステークホルダー（投資家、従業員、顧客、コミュニティ）がどのような課題に関心を抱いているかを、3,200に及ぶ項目から総合的に診断します。

ベースになっているのは、弊社代表がESG/SDGs評価インデックス作成等を通して蓄積してきた経験とデータです。

診断結果を既存の取り組みやご関心に合わせてカスタマイズすることもできます。`,
    },
    {
      subTitle: '効果的・効率的な活動をするには？',
      title: '課題解決の実践者と出会う',
      image: '/images/diagnosis/main3.png',
      text: `診断結果に登場する社会課題をクリックしていただくと、それぞれの課題について詳しく知ることができます。

自社のみで効果的な社会的活動を行うことが難しい場合もあるでしょう。診断結果をもとに、社会的事業のパートナーになりうる団体さま（課題の解決に取り組む実践者の皆さま）を知ることもできます。

また、私たちが運営する『サステナNet』では、会社のあらゆる社会的活動に関する情報を1か所にまとめ、社内外に効率的に発信することができます。ぜひそちらもご活用ください。`,
    },
  ];

  const onClickStart = () => {
    router.push('/diagnosis/start');
  };

  return (
    <Wrapper>
      <ContactButtonWrapper>
        <ContactButton link='https://forms.gle/iUkDa6uovWBWBMUQ7' />
      </ContactButtonWrapper>
      <StartButtonWrapper>
        <StartDiagnosisButton onClick={onClickStart} />
      </StartButtonWrapper>
      <Header />
      <Top>
        <DiagnosisHeader />
        <TopImage src='/images/diagnosis/top.png' />
      </Top>
      <Main>
        <MainTop>
          ICHI
          COMMONSの豊富な経験とデータをもとに、御社が最も貢献できる社会課題・SDGsを診断。社会的事業のパートナー候補を知ることもできます。
        </MainTop>
        <Steps>
          <StepImage src='/images/diagnosis/steps.png' />
        </Steps>
        {contents.map((content, i) => (
          <MainContent key={i}>
            <MainContentSubTitle>{content.subTitle}</MainContentSubTitle>
            <MainContentTitle>{content.title}</MainContentTitle>
            <MainContentImage src={content.image} />
            <MainContentText>{content.text}</MainContentText>
          </MainContent>
        ))}
      </Main>
      <Footer />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContactButtonWrapper = styled.div`
  position: fixed;
  right: 50px;
  bottom: 30px;
  z-index: 250;

  ${media.sp`
    right: 20px;
    bottom: 80px;
    width: 100%;
  `}
`;

const StartButtonWrapper = styled.div`
  position: fixed;
  width: 100%;
  max-width: 530px;
  bottom: 50px;

  ${media.mid`
    bottom: 20px;
    padding-left: 40px;
  `}
`;

const Top = styled.div`
  padding-bottom: 140px;
  background: #c3ebec;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const TopImage = styled.img`
  margin-top: -10px;
  width: 436px;

  ${media.mid`
    width: 300px;
  `}
`;

const Main = styled.div`
  margin-bottom: 72px;
  padding: 24px 150px 100px;
  margin-top: -100px;
  width: 600px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.26);

  ${media.mid`
    padding: 24px 20px 100px;
    width: calc(100% - 30px - 40px);
  `}
`;

const MainTop = styled.h4`
  margin: 0 -90px;
  font-size: 18px;
  line-height: 26px;
  color: #2b2b2b;

  ${media.mid`
    margin: 0;
    font-size: 16px;
    line-height: 20px;
  `}
`;

const Steps = styled.div`
  margin: 30px -90px;
  background: #def3f4;
  height: 282px;
  display: flex;
  justify-content: center;
  align-items: center;

  ${media.mid`
    margin: 20px 0;
  `}

  ${media.sp`
    height: auto;
    padding: 20px;
  `}
`;

const StepImage = styled.img`
  width: 537px;

  ${media.sp`
    width: calc(100% - 40px);
  `}
`;

const MainContent = styled.div`
  margin-top: 70px;

  ${media.mid`
    margin-top: 40px;
  `}
`;

const MainContentSubTitle = styled.h4`
  font-size: 20px;
  line-height: 29px;
  color: #848484;
  font-weight: bold;

  ${media.mid`
    font-size: 16p;
    line-height: 24px;
  `}
`;

const MainContentTitle = styled.h3`
  font-size: 33px;
  line-height: 48px;
  color: #2b2b2b;
  font-weight: bold;

  ${media.mid`
    font-size: 24p;
    line-height: 34px;
  `}
`;

const MainContentImage = styled.img`
  margin-top: 18px;
  width: 596px;

  ${media.mid`
    width: 100%;
  `}
`;

const MainContentText = styled.p`
  margin-top: 18px;
  font-size: 16px;
  line-height: 24px;
  color: #2b2b2b;
`;
